import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import { isPracticalType } from '../../utils/practicalResults';
import { ACCENT_PROJECT, ACCENT_ACADEMIC } from '../../lib/personAccents';
import TableRowsSkeleton from './TableRowsSkeleton';

const PAGE_SIZE = 10;

export interface EntryItem {
  id?: number;
  name: string;
  type: string;
  year: number;
  description: string;
  is_practical?: boolean;
}

interface EntryListCardLabels {
  section: string;
  showButton: string;
  hideButton: string;
  filterPlaceholder: string;
  nameColumn: string;
  typeColumn: string;
  yearColumn: string;
  noDescription: string;
  emptyTitle: string;
  emptyDescription: string;
  noMatch: string;
  viewDetails: string;
  practicalOnly?: string;
}

type AccentMode = 'practical' | 'project' | 'none';
type Accent = 'practical' | 'academic' | 'project' | 'neutral';

const BADGE_CLASS: Record<Accent, string> = {
  practical: 'bg-primary text-primary-foreground border-transparent font-normal hover:bg-primary',
  academic: 'text-white border-transparent font-normal',
  project: 'text-white border-transparent font-normal',
  neutral: 'font-normal',
};

interface EntryListCardProps {
  items: EntryItem[];
  loading: boolean | null;
  error: string | null;
  onNavigate: (path: string, e: React.MouseEvent) => void;
  detailPathPrefix: string;
  labels: EntryListCardLabels;
  accentMode?: AccentMode;
  enablePracticalFilter?: boolean;
}

function isPractical(item: EntryItem): boolean {
  return Boolean(item.is_practical) || isPracticalType(item.type);
}

function getAccent(mode: AccentMode, item: EntryItem): Accent {
  if (mode === 'practical') return isPractical(item) ? 'practical' : 'academic';
  if (mode === 'project') return 'project';
  return 'neutral';
}

function compactPages(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const result: (number | 'ellipsis')[] = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  if (left > 2) result.push('ellipsis');
  for (let p = left; p <= right; p++) result.push(p);
  if (right < total - 1) result.push('ellipsis');
  result.push(total);
  return result;
}

export default function EntryListCard({
  items,
  loading,
  error,
  onNavigate,
  detailPathPrefix,
  labels,
  accentMode = 'none',
  enablePracticalFilter = false,
}: EntryListCardProps) {
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [filter, setFilter] = useState('');
  const [practicalOnly, setPracticalOnly] = useState(false);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return items.filter(item => {
      if (enablePracticalFilter && practicalOnly && !isPractical(item)) return false;
      if (!f) return true;
      return (
        (item.name && item.name.toLowerCase().includes(f)) ||
        (item.type && item.type.toLowerCase().includes(f)) ||
        (item.year && String(item.year).toLowerCase().includes(f)) ||
        (item.description && item.description.toLowerCase().includes(f))
      );
    });
  }, [items, filter, practicalOnly, enablePracticalFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
    setExpanded([]);
  }, [filter, practicalOnly]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paginated = useMemo(() => {
    if (filtered.length <= PAGE_SIZE) return filtered;
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const toggleRow = (idx: number) =>
    setExpanded(prev => (prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]));

  const goToPage = (target: number, e: React.MouseEvent) => {
    e.preventDefault();
    const clamped = Math.max(1, Math.min(totalPages, target));
    setPage(clamped);
    setExpanded([]);
  };

  return (
    <Card className="mb-6 border-ctu-border shadow-none">
      <CardHeader className="flex-row items-center justify-between space-y-0 px-6 py-5">
        <CardTitle className="flex items-baseline gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
            {labels.section}
          </span>
          <span className="text-[11px] font-mono tabular-nums text-ctu-text-muted">
            {items.length}
          </span>
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={() => setShow(s => !s)}>
          {show ? labels.hideButton : labels.showButton}
          <ChevronDown
            className={cn(
              'ml-2 h-4 w-4 text-ctu-text-muted transition-transform',
              show && 'rotate-180',
            )}
            aria-hidden
          />
        </Button>
      </CardHeader>

      {show && (
        <CardContent className="px-6 pt-2 pb-6">
          {loading && <TableRowsSkeleton rows={4} withFilter />}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!loading && !error && items.length > 0 && (
            <>
              <div className="mb-5 flex flex-wrap items-center gap-x-5 gap-y-3">
                <Input
                  type="text"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  placeholder={labels.filterPlaceholder}
                  className="max-w-[400px] flex-1 min-w-[220px]"
                />
                {enablePracticalFilter && labels.practicalOnly && (
                  <label
                    htmlFor="practical-only-filter"
                    className="flex shrink-0 items-center gap-2 text-xs font-medium text-ctu-text-muted hover:text-ctu-text cursor-pointer select-none"
                  >
                    <Checkbox
                      id="practical-only-filter"
                      checked={practicalOnly}
                      onCheckedChange={(value: boolean | 'indeterminate') =>
                        setPracticalOnly(value === true)
                      }
                    />
                    {labels.practicalOnly}
                  </label>
                )}
              </div>

              <Table className="table-fixed w-full">
                <colgroup>
                  <col className="w-[60%]" />
                  <col className="w-[22%]" />
                  <col className="w-[12%]" />
                  <col className="w-[6%]" />
                </colgroup>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[11px] font-semibold text-ctu-text-muted uppercase tracking-[0.14em]">
                      {labels.nameColumn}
                    </TableHead>
                    <TableHead className="text-[11px] font-semibold text-ctu-text-muted uppercase tracking-[0.14em]">
                      {labels.typeColumn}
                    </TableHead>
                    <TableHead className="text-[11px] font-semibold text-ctu-text-muted uppercase tracking-[0.14em] text-right">
                      {labels.yearColumn}
                    </TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginated.map((item, idx) => {
                    const expandedKey = (page - 1) * PAGE_SIZE + idx;
                    const isExpanded = expanded.includes(expandedKey);
                    const detailPath =
                      typeof item.id === 'number' ? `${detailPathPrefix}/${item.id}` : null;
                    const accent = getAccent(accentMode, item);
                    const badgeClass = BADGE_CLASS[accent];
                    const badgeStyle =
                      accent === 'project'
                        ? { backgroundColor: ACCENT_PROJECT }
                        : accent === 'academic'
                          ? { backgroundColor: ACCENT_ACADEMIC }
                          : undefined;
                    return (
                      <React.Fragment key={`${item.id ?? expandedKey}-${expandedKey}`}>
                        <TableRow
                          onClick={() => toggleRow(expandedKey)}
                          className="cursor-pointer hover:bg-muted/40"
                        >
                          <TableCell className="text-sm text-ctu-text py-3">
                            {detailPath ? (
                              <a
                                href={detailPath}
                                onClick={e => {
                                  e.stopPropagation();
                                  onNavigate(detailPath, e);
                                }}
                                className="font-medium text-ctu-text no-underline hover:text-primary hover:underline"
                              >
                                {item.name}
                              </a>
                            ) : (
                              <span className="font-medium">{item.name}</span>
                            )}
                          </TableCell>
                          <TableCell className="py-3">
                            <Badge
                              variant="outline"
                              className={badgeClass}
                              style={badgeStyle}
                            >
                              {item.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-ctu-text-muted text-right font-mono tabular-nums py-3">
                            {item.year}
                          </TableCell>
                          <TableCell className="text-center py-3">
                            <ChevronRight
                              className={cn(
                                'mx-auto h-4 w-4 text-ctu-text-muted transition-transform',
                                isExpanded && 'rotate-90',
                              )}
                              aria-hidden
                            />
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow className="hover:bg-transparent">
                            <TableCell
                              colSpan={4}
                              className="bg-muted/30 py-4"
                            >
                              <div className="flex items-start justify-between gap-4">
                                <p className="text-ctu-text-muted text-sm leading-relaxed flex-1">
                                  {item.description || labels.noDescription}
                                </p>
                                {detailPath && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="shrink-0 h-8"
                                    onClick={e => {
                                      e.stopPropagation();
                                      onNavigate(detailPath, e);
                                    }}
                                  >
                                    {labels.viewDetails}
                                    <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>

              {filtered.length === 0 && (
                <Empty className="bg-muted/20 mt-4">
                  <EmptyHeader>
                    <EmptyTitle className="text-base">{labels.noMatch}</EmptyTitle>
                  </EmptyHeader>
                </Empty>
              )}

              {totalPages > 1 && (
                <Pagination className="mt-6">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={e => goToPage(page - 1, e)}
                        className={cn(page === 1 && 'pointer-events-none opacity-40')}
                        aria-disabled={page === 1}
                      />
                    </PaginationItem>
                    {compactPages(page, totalPages).map((p, i) => (
                      <PaginationItem key={`${p}-${i}`}>
                        {p === 'ellipsis' ? (
                          <PaginationEllipsis />
                        ) : (
                          <PaginationLink
                            href="#"
                            isActive={p === page}
                            onClick={e => goToPage(p, e)}
                          >
                            {p}
                          </PaginationLink>
                        )}
                      </PaginationItem>
                    ))}
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={e => goToPage(page + 1, e)}
                        className={cn(page === totalPages && 'pointer-events-none opacity-40')}
                        aria-disabled={page === totalPages}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}

          {!loading && !error && items.length === 0 && (
            <Empty className="bg-muted/20">
              <EmptyHeader>
                <EmptyTitle className="text-base">{labels.emptyTitle}</EmptyTitle>
                <EmptyDescription>{labels.emptyDescription}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </CardContent>
      )}
    </Card>
  );
}
